import React, { useEffect, useState } from 'react';
import fileDownload from 'js-file-download';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import api from '../services/api';
import type StudyDocumentModel from '../models/StudyDocumentModel';
import type StudyParticipantModel from '../models/StudyParticipantModel';
import getParticipantDocument from '../services/api/endpoints/getParticipantDocument';
import Table from './Table';
import CreateStudyDocumentForm from './CreateStudyDocumentForm';

type PropTypes = {
    participant?: StudyParticipantModel;
};

const StudyDocuments = ({ participant }: PropTypes) => {
    const [documents, setDocuments] = useState<StudyDocumentModel[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<
        StudyDocumentModel | undefined
    >(undefined);
    const [addingDocument, setAddingDocument] = useState(false);

    const pullDocuments = async () => {
        const { documents, success } = await api.getParticipantDocuments(
            participant?.id,
        );
        if (success) {
            setDocuments(documents);
        }
    };

    useEffect(() => {
        void pullDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteDocument = async (documentId: number) => {
        const { success } = await api.deleteParticipantDocument(documentId);
        if (success) {
            setSelectedDocument(undefined);
            void pullDocuments();
        }
    };

    const onDocumentCreated = () => {
        setAddingDocument(false);
        void pullDocuments();
    };

    return (
        <div className="mt-4">
            <div className="flex items-center mb-4">
                <div className="text-primary text-3xl">Study Documents</div>
                {participant && (
                    <button
                        type="button"
                        className="btn btn-primary ml-4"
                        onClick={async () => {
                            setAddingDocument(true);
                        }}
                    >
                        Add Document
                    </button>
                )}
            </div>
            <Table
                title="Documents"
                data={documents}
                columns={['id', 'participant_id', 'name']}
                onClick={(study) => {
                    setSelectedDocument(study as StudyDocumentModel);
                }}
            />

            {selectedDocument !== undefined && (
                <Dialog
                    visible={selectedDocument !== undefined}
                    header={selectedDocument.name}
                    onHide={() => {
                        setSelectedDocument(undefined);
                    }}
                >
                    <button
                        type="button"
                        className="btn btn-primary mt-4 mr-4"
                        onClick={async () => {
                            const file = await getParticipantDocument(
                                selectedDocument.id,
                            );
                            fileDownload(file, selectedDocument.name);
                        }}
                    >
                        Download Document
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary mt-4"
                        onClick={() => {
                            confirmDialog({
                                message: `Are you sure you want to delete document ${selectedDocument.name}?`,
                                accept: async () =>
                                    deleteDocument(selectedDocument.id),
                            });
                        }}
                    >
                        Delete Document
                    </button>
                </Dialog>
            )}
            {addingDocument && participant && (
                <Dialog
                    visible={addingDocument}
                    header="Add Document"
                    onHide={() => {
                        setAddingDocument(false);
                    }}
                >
                    <CreateStudyDocumentForm
                        participant={participant}
                        onDocumentCreated={onDocumentCreated}
                    />
                </Dialog>
            )}
        </div>
    );
};

export default StudyDocuments;
